/**
 * HEIC support for browsers that have no HEIC codec.
 *
 * Safari and iOS decode HEIC natively, so nothing here should ever run
 * there. Chrome and Firefox cannot decode it at all: the <img> fires
 * error and createImageBitmap throws, which is why the decoder is a
 * dynamic import. It is around 3 MB and is fetched only after a real
 * decode has already failed on a file that really is HEIC.
 */

/**
 * `ftyp` brands that mean HEIF. `mif1` and `msf1` are the generic image
 * and sequence brands Apple writes for some captures, so they belong
 * here alongside the explicit `heic` family.
 */
const HEIF_BRANDS = new Set([
  'heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs',
  'mif1', 'msf1',
])

/**
 * Sniffs the ISO base media header rather than trusting file.type,
 * which is empty often enough on desktop file pickers to matter.
 */
export async function looksLikeHeic(file: File): Promise<boolean> {
  try {
    const head = new Uint8Array(await file.slice(0, 12).arrayBuffer())
    if (head.length < 12) return false
    const ascii = (from: number, to: number) =>
      String.fromCharCode(...head.slice(from, to))
    return ascii(4, 8) === 'ftyp' && HEIF_BRANDS.has(ascii(8, 12))
  } catch {
    return false
  }
}

/**
 * Decodes HEIC to a JPEG the rest of the pipeline can treat normally.
 * Quality matches the crop overlay's own export, since this result is
 * re-encoded to WebP later anyway and there is no point compounding
 * loss twice at a lower setting.
 */
export async function heicToJpeg(file: File): Promise<File> {
  const { heicTo } = await import('heic-to')
  const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 })
  const name = file.name.replace(/\.[^.]+$/, '') || 'photo'
  return new File([blob], `${name}.jpg`, { type: 'image/jpeg' })
}
